import React, {Component} from 'react';
import {Link, Navigate, useNavigate} from 'react-router-dom';
import {withTranslation} from 'react-i18next';
import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';
import {AccessService} from '../../utils/AccessService';
import {useFicha21} from '../../context/UserModulesContext';
import {usePendingFactsFor, usePendingWork} from '../../context/PendingWorkContext';
import {Spinner} from '../Spinner';
import {ClientHeader} from './ClientHeader';
import {ClientTabs} from './ClientTabs';
import {MemoryFactsView} from './memory/MemoryFactsView';
import {MemorySetupView} from './memory/MemorySetupView';
import {MemoryNotesView} from './memory/MemoryNotesView';
import {MemoryBitacoraView} from './memory/MemoryBitacoraView';
import {MemoryResumenView} from './memory/MemoryResumenView';
import {MemorySourcesView} from './memory/MemorySourcesView';
import {DocumentKindModal} from './memory/DocumentKindModal';
import {BoosterStage} from './ia_booster/BoosterStage';
import {activeBoosterStep, boosterFlowPathForView, boosterStepsFor} from './ia_booster/boosterSteps';

// Sub-vistas por nivel de acceso a la Memoria (el nivel sale de la suscripción de la agencia).
// El Resumen ("qué sabe Dashbo") es el default, la Bitácora vive adentro como vista temporal y la
// navegación se orienta al trabajo — Resumen · Bitácora · Hechos · Notas · Fuentes
// (Documentos+Conexiones fusionadas) + botón "+ Agregar" (Cargar deja de ser pill; sigue accesible
// como pantalla del flujo Reunión). MEDIO oculta "Hechos": esa capa no corre en ese nivel.
//
// Esta navegación nació con la ficha 2.1 y hasta 2026-08-03 estaba atada al rediseño: una agencia
// con la Memoria contratada pero sin la 2.1 veía la generación anterior (Hechos · Documentos ·
// Notas · Conexiones · Cargar), sin Resumen ni el checklist de contexto — o sea, sin la pantalla
// que explica la feature que le acaban de vender. Desde que la Memoria se habilita por suscripción
// (card UkzdiHRb) ese caso dejó de ser una excepción del piloto y pasó a ser el camino normal de
// toda agencia nueva, así que la navegación nueva es LA navegación: es parte de la feature, no del
// rediseño. El marco (sidebar/topbar) sigue siendo otra decisión, gobernada por `ui_v2`.
const SUB_VIEWS_BY_LEVEL = {
    [AccessService.MEMORY_LEVEL_TOTAL]: ['resumen', 'bitacora', 'facts', 'notes', 'sources'],
    [AccessService.MEMORY_LEVEL_MEDIO]: ['resumen', 'bitacora', 'notes', 'sources'],
    [AccessService.MEMORY_LEVEL_OFF]: ['bitacora'],
};
// Vistas accesibles por URL sin pill propia (destinos del menú "+ Agregar").
const HIDDEN_VIEWS = ['setup'];

// Qué se agrega DESDE cada vista. El menú "+ Agregar" abre con eso, porque el mismo botón en tres
// pestañas distintas desplegaba el MISMO menú: en el piloto, apretarlo parado en Hechos terminó
// subiendo documentos sin que la usuaria se diera cuenta (card JH1GK3r9). Las vistas de lectura
// (Resumen, Bitácora) no tienen acción propia: ahí el menú abre directo con el material.
const PRIMARY_ADD_BY_VIEW = {
    facts: 'fact',
    notes: 'note',
    sources: 'document',
    setup: 'document',
};

// Contenedor del tab "Memoria del cliente". Header + ClientTabs + sub-navegación interna
// (pills) entre las vistas del nivel. El badge de pendientes sobre "Hechos" (sólo TOTAL) deja
// ver cuántos hechos esperan revisión sin entrar.
class ClientMemoryTabContentClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            name: '',
            addMenuOpen: false,
            kindPickerOpen: false,
            // Sube cada vez que entra material por la Barra Total o por el drop sobre el orbe.
            // Baja como prop a la vista de Fuentes, que lo usa para re-pedir su listado en
            // silencio. Sin esto la lista de material —que está en la MISMA pantalla, debajo del
            // escenario— no mostraba lo recién subido hasta recargar.
            ingestToken: 0,
        };
        this.handleIngested = this.handleIngested.bind(this);
    }

    handleIngested() {
        this.setState(state => ({ingestToken: state.ingestToken + 1}));
    }

    componentDidMount() {
        this.getClientData();
    }

    getClientData() {
        new APIClient(API_URL + 'client/' + this.props.clientId).call('GET').then((data) => {
            if (data && data.status === 'OK') {
                this.setState({name: data.data['name']});
            }
        });
    }

    // Hechos esperando revisión. UNA sola fuente: el poller global — el mismo número que pinta el
    // badge del tab bar, cuarenta píxeles más arriba. Esta pantalla pedía la lista COMPLETA de
    // hechos pendientes sólo para hacerle `.length`, con su propio ciclo de vida.
    //
    // Al curar, `MemoryFactsView` avisa y el valor se corrige en el CONTEXTO (optimista), no en un
    // state local: un espejo local nunca volvía a ceder ante el poller, así que un hecho nuevo
    // llegado mientras el usuario estaba en la pantalla dejaba los dos badges con números distintos
    // (review 2026-07-31). El override del contexto se limpia solo en cada poll exitoso.
    pendingCount() {
        if (this.props.clientMemoryLevel !== AccessService.MEMORY_LEVEL_TOTAL) return 0;
        return this.props.pendingFacts || 0;
    }

    hasModule(moduleName) {
        return (this.props.modules || []).includes(moduleName);
    }

    hasActivityLogModule() {
        return this.hasModule(AccessService.MODULE_ACTIVITY_LOG);
    }

    hasIAModules() {
        return this.hasModule(AccessService.MODULE_AGENT) || this.hasModule(AccessService.MODULE_DASHBO_MCP);
    }

    hasBitacora() {
        // Gate de la vista Bitácora dentro de la Memoria: SOLO el módulo (D12) — el punto es que el
        // equipo de la agencia (no-SuperAdmin) la use. El tab standalone legacy conserva su gate
        // SuperAdmin aparte.
        return this.hasActivityLogModule();
    }

    hasMemoryLevel() {
        const level = this.props.clientMemoryLevel;
        return level === AccessService.MEMORY_LEVEL_MEDIO || level === AccessService.MEMORY_LEVEL_TOTAL;
    }

    subViews() {
        const views = SUB_VIEWS_BY_LEVEL[this.props.clientMemoryLevel] || [];
        return views.filter(view => view !== 'bitacora' || this.hasBitacora());
    }

    accessibleViews() {
        // Las pills + las vistas ocultas accesibles por URL (flujo del "+ Agregar").
        if (!this.hasMemoryLevel()) return this.subViews();
        return this.subViews().concat(HIDDEN_VIEWS);
    }

    activeView() {
        const views = this.accessibleViews();
        const view = this.props.view;
        return views.includes(view) ? view : views[0];
    }

    addMenuItems() {
        // Destinos del "+ Agregar": atajos a los flujos de alimentación que ya existen en cada
        // vista. Con nivel MEDIO no hay Hechos manuales.
        //
        // "Subir material" no navega: abre el selector de tipo. Es la ÚNICA puerta permanente a la
        // carga tipada — los CTA del checklist son la otra, pero ese se auto-oculta cuando el
        // contexto central está completo, y sus cuatro tipos secundarios (propuesta, marca,
        // research, FAQ) sólo se ven en el empty state. Sin esta entrada, subir un manual de marca
        // con su tipo deja de ser posible apenas la agencia carga el primer hecho.
        //
        // Cada entrada lleva su descripción: los labels solos («Hecho», «Nota», «Documento del
        // cliente») no dicen en qué se diferencian ni a qué pantalla llevan.
        const {t} = this.props;
        const clientId = this.props.clientId;
        const item = (key, rest) => Object.assign({
            key,
            label: t('client_memory.add_menu.' + key),
            desc: t('client_memory.add_menu.desc.' + key),
        }, rest);
        const items = [
            item('meeting', {to: `/client/${clientId}/memory/setup`}),
            item('document', {action: 'pick-kind'}),
            item('url', {to: `/client/${clientId}/memory/setup?mode=url`}),
            item('drive', {to: `/client/${clientId}/memory/sources?connect=drive`}),
            item('note', {to: `/client/${clientId}/memory/notes`}),
        ];
        if (this.props.clientMemoryLevel === AccessService.MEMORY_LEVEL_TOTAL) {
            items.push(item('fact', {to: `/client/${clientId}/memory/facts`}));
        }
        return items;
    }

    // El menú en dos secciones: primero lo que se agrega DESDE la pestaña donde está parado el
    // usuario, después el resto del material. Sin esa separación, elegir «Nota» estando en Hechos
    // muda de pantalla sin avisar — que es lo que pasó en el piloto.
    addMenuSections() {
        const {t} = this.props;
        const view = this.activeView();
        const all = this.addMenuItems();
        const primary = all.find((each) => each.key === PRIMARY_ADD_BY_VIEW[view]);
        const sections = [];
        if (primary) {
            sections.push({
                key: 'here',
                // El nombre de la pestaña sale de la MISMA clave que la pill de arriba: si el menú
                // la nombrara distinto, no se leería como «acá».
                title: t('client_memory.add_menu.in_view', {view: t('client_memory.subnav.' + view)}),
                items: [primary],
            });
        }
        const rest = all.filter((each) => each !== primary);
        if (rest.length > 0) {
            sections.push({
                key: 'material',
                title: t('client_memory.add_menu.material_title'),
                items: rest,
            });
        }
        return sections;
    }

    renderAddMenuItem(item) {
        const body = (
            <>
                <span className="cmem-add-item-label">{item.label}</span>
                <span className="cmem-add-item-desc">{item.desc}</span>
            </>
        );
        if (item.action === 'pick-kind') {
            return (
                <button
                    key={item.key}
                    type="button"
                    className="cmem-add-item"
                    onClick={() => this.setState({addMenuOpen: false, kindPickerOpen: true})}
                >
                    {body}
                </button>
            );
        }
        return (
            <Link
                key={item.key}
                className="cmem-add-item"
                to={item.to}
                onClick={() => this.setState({addMenuOpen: false})}
            >
                {body}
            </Link>
        );
    }

    renderAddMenu() {
        const {t} = this.props;
        if (!this.hasMemoryLevel()) return null;
        return (
            <div className="cmem-add">
                <button
                    type="button"
                    className="btn -alt cmem-add-button"
                    onClick={() => this.setState({addMenuOpen: !this.state.addMenuOpen})}
                    aria-expanded={this.state.addMenuOpen}
                >
                    {t('client_memory.add_menu.button')}
                </button>
                {this.state.addMenuOpen && (
                    <div className="cmem-add-menu">
                        {this.addMenuSections().map((section) => (
                            <div key={section.key} className="cmem-add-section">
                                <div className="cmem-add-section-title">{section.title}</div>
                                {section.items.map((each) => this.renderAddMenuItem(each))}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    renderKindPicker() {
        // Elegir el tipo y caer en la carga con ese tipo ya fijado: el mismo destino al que llevan
        // los CTA del checklist, así que las dos puertas terminan en la misma pantalla.
        return (
            <DocumentKindModal
                isOpen={this.state.kindPickerOpen}
                toggle={() => this.setState({kindPickerOpen: false})}
                onConfirm={(kind) => {
                    this.setState({kindPickerOpen: false});
                    this.props.navigate(`/client/${this.props.clientId}/memory/setup?kind=${kind}`);
                }}
            />
        );
    }

    // El escenario del Recorrido corona la pantalla en el marco 2.1 y NO existe en el clásico,
    // donde esta pantalla se sigue llegando por su pestaña y el Recorrido no es la navegación.
    // Es la regla que sostiene la convivencia: v2 es aditivo.
    //
    // Con la one-page esto quedó dibujándose en UNA sola pantalla —y por eso NO se retiró—: el
    // flujo de carga (`/memory/setup`), la única vista de este tab que no migra al Recorrido
    // (`boosterFlowPathForView` devuelve `null`) y por lo tanto la única que no redirige. Sin el
    // hilo acá, la carga de material quedaría sin camino de vuelta a los actos.
    renderBoosterStage() {
        if (!this.props.ficha21) return null;
        const clientId = this.props.clientId;
        const view = this.activeView();
        // El paso se resuelve con la MISMA función que usa el sidebar, pasándole la ruta que esta
        // vista representa — no con un `switch` propio sobre el nombre de la sub-vista.
        const path = view ? `/client/${clientId}/memory/${view}` : `/client/${clientId}/memory`;
        return (
            <BoosterStage
                clientId={clientId}
                clientName={this.state.name}
                step={activeBoosterStep(path)}
                steps={boosterStepsFor({
                    clientMemoryLevel: this.props.clientMemoryLevel,
                    modules: this.props.modules,
                })}
                pendingCount={this.pendingCount()}
                hasIa={this.hasIAModules()}
                onIngested={this.handleIngested}
            />
        );
    }

    renderSubNav() {
        const {t} = this.props;
        const active = this.activeView();
        const pendingCount = this.pendingCount();
        return (
            <nav className="cmem-subnav">
                {this.subViews().map((view) => {
                    const isActive = view === active;
                    const showBadge = view === 'facts' && pendingCount > 0;
                    return (
                        <Link
                            key={view}
                            className={`cmem-pill ${isActive ? '-active' : ''}`}
                            to={`/client/${this.props.clientId}/memory/${view}`}
                        >
                            {t(`client_memory.subnav.${view}`)}
                            {showBadge && <span className="cmem-pill-badge">{pendingCount}</span>}
                        </Link>
                    );
                })}
                {this.renderAddMenu()}
            </nav>
        );
    }

    renderActiveView() {
        const {t} = this.props;
        const active = this.activeView();
        if (active === 'resumen') {
            return (
                <MemoryResumenView
                    clientId={this.props.clientId}
                    clientName={this.state.name}
                    showIACta={this.hasIAModules()}
                />
            );
        }
        if (active === 'bitacora') {
            return <MemoryBitacoraView clientId={this.props.clientId}/>;
        }
        if (active === 'sources') {
            // El token del refresco viaja como PROP, no como `key`: con `key` la vista se
            // remontaba entera en cada ingesta y el usuario perdía scroll, filtros y cualquier
            // formulario a medio llenar. Sólo lo recibe esta vista porque la Barra Total y el drop
            // sobre el orbe se dibujan únicamente en el paso Alimenta — no hay forma de ingerir
            // material parado en otra.
            return <MemorySourcesView clientId={this.props.clientId} clientName={this.state.name}
                                      refreshToken={this.state.ingestToken}/>;
        }
        if (active === 'facts') {
            return (
                <MemoryFactsView
                    clientId={this.props.clientId}
                    onPendingCountChange={this.props.onPendingFactsChange}
                />
            );
        }
        if (active === 'setup') {
            return <MemorySetupView clientId={this.props.clientId}/>;
        }
        if (active === 'notes') {
            return <MemoryNotesView clientId={this.props.clientId}/>;
        }
        return (
            <div className="cmem-placeholder">
                <p>{t('client_memory.coming_soon')}</p>
            </div>
        );
    }

    render() {
        const {t} = this.props;

        // Cargando el nivel de acceso: no decidir todavía (evita un parpadeo redirect→contenido).
        // El provider ya da OFF por default, así que la señal de "todavía no sé" es `loaded`.
        if (!this.props.modulesLoaded) {
            return (
                <section className="section-content">
                    <ClientHeader name={this.state.name}/>
                    <ClientTabs clientId={this.props.clientId}/>
                    <div className="section-body">
                        <div className="box-loader"><Spinner color="positive"/></div>
                    </div>
                </section>
            );
        }

        const views = this.subViews();
        // Sin ninguna vista disponible (Memoria OFF y sin Bitácora): fuera del tab.
        if (views.length === 0) {
            return <Navigate to={`/client/${this.props.clientId}/monitoring`} replace/>;
        }
        // Pidió por URL una vista no disponible (ej. 'facts' en MEDIO) → default del nivel.
        const requested = this.props.view;
        if (requested && !this.accessibleViews().includes(requested)) {
            return <Navigate to={`/client/${this.props.clientId}/memory/${views[0]}`} replace/>;
        }
        // Desde la one-page (card LDdcpf52) estas rutas siguen vivas pero REDIRIGEN al acto que les
        // corresponde: son links que están en producción —el badge de la cola del Listado v2, los
        // CTAs del Panel, la campanita, el Command Center— y el contrato
        // `?layers=&date_from=&date_to=` con el que se abre la Bitácora ya filtrada, que por eso
        // viaja entero al destino.
        //
        // Va ÚLTIMO a propósito, y traduce `activeView()` (ya normalizada), no `this.props.view`:
        // los dos redirects de arriba resuelven primero A DÓNDE VA ESTE USUARIO, y recién sobre esa
        // respuesta se busca el ancla. Al revés, sin Memoria ni Bitácora `activeView()` sería
        // `undefined` y el destino saldría sin ancla en vez de irse del tab, y una vista que el
        // nivel no tiene —`facts` en MEDIO— apuntaría a `#revisa`, un acto que la one-page no
        // dibuja para esa agencia. Pasando por el default del nivel, el ancla siempre existe.
        if (this.props.ficha21) {
            const to = boosterFlowPathForView(
                this.activeView(), this.props.clientId, window.location.search);
            if (to) return <Navigate to={to} replace/>;
        }

        return (
            <section className="section-content">
                <ClientHeader name={this.state.name}/>
                <ClientTabs clientId={this.props.clientId}/>

                {this.renderBoosterStage()}

                <div className="section-body">
                    <div className="box">
                        <header className="box-header">
                            <h2 className="box-title">{t('client_memory.title')}</h2>
                        </header>
                        {this.renderSubNav()}
                        <div className="cmem-view">
                            {this.renderActiveView()}
                        </div>
                    </div>
                </div>
                {this.renderKindPicker()}
            </section>
        );
    }
}

const ClientMemoryTabContentInner = withTranslation()(ClientMemoryTabContentClass);

// El nivel de Memoria y los módulos salen del provider (fuente única). El nivel NO se va con el
// switch al clásico —la Memoria se contrata, no se rollea— y desde 2026-08-03 la NAVEGACIÓN tampoco:
// esta pantalla se ve igual con el marco nuevo y con el clásico.
export function ClientMemoryTabContent(props) {
    // `ficha21` decide si se dibuja el escenario del Recorrido (marco 2.1). El NIVEL de memoria y
    // la navegación de esta pantalla no dependen de él — se contratan, no se rollean.
    const {ficha21, clientMemoryLevel, modules, loaded} = useFicha21();
    // El selector de tipo del "+ Agregar" navega a la carga; el contenido es un class component.
    const navigate = useNavigate();
    // El conteo de la cola sale del poller global; al curar se corrige en el acto (optimista) para
    // que el badge del tab bar no quede desfasado hasta el próximo poll.
    const pendingFacts = usePendingFactsFor(props.clientId);
    const {overridePendingFacts} = usePendingWork();
    return <ClientMemoryTabContentInner {...props} clientMemoryLevel={clientMemoryLevel}
                                        ficha21={ficha21}
                                        modules={modules} modulesLoaded={loaded}
                                        navigate={navigate}
                                        pendingFacts={pendingFacts}
                                        onPendingFactsChange={
                                            (n) => overridePendingFacts(props.clientId, n)}/>;
}
